import { type PropsWithChildren, useRef, useState } from 'react';

import { GlobalFontSizeContext } from '../../core/components/single-value-renderer/contexts/global-font-size.context.js';

export const GlobalFontSizeProvider = ({
  children,
  valueCount,
  labelCount,
  trendCount,
}: PropsWithChildren<{
  valueCount: number;
  labelCount: number;
  trendCount: number;
}>) => {
  const valueFontSizes = useRef<Map<string, number>>(new Map<string, number>());
  const labelFontSizes = useRef<Map<string, number>>(new Map<string, number>());
  const trendFontSizes = useRef<Map<string, number>>(new Map<string, number>());

  const isValueReady = useRef<boolean>(false);
  const isLabelReady = useRef<boolean>(labelCount === 0);
  const isTrendReady = useRef<boolean>(trendCount === 0);

  const [fontSizes, setFontSizes] = useState<{
    label: number;
    value: number;
    trend: number;
  }>();

  const reset = () => {
    valueFontSizes.current.clear();
    labelFontSizes.current.clear();
    trendFontSizes.current.clear();

    isValueReady.current = false;
    isLabelReady.current = labelCount === 0;
    isTrendReady.current = trendCount === 0;
  };

  reset(); // reset font sizes every time the provider re-renders

  const updateFontSizes = () => {
    if (isValueReady.current && isLabelReady.current && isTrendReady.current) {
      const value = Math.min(...valueFontSizes.current.values());

      if (value > 0) {
        const label = Math.min(...labelFontSizes.current.values());
        const trend = Math.min(...trendFontSizes.current.values());

        setFontSizes({
          label,
          value,
          trend,
        });
      }
    }
  };

  const updateValueFontSize = (id: string, fontSize: number) => {
    valueFontSizes.current.set(id, fontSize);

    if (valueFontSizes.current.size === valueCount) {
      isValueReady.current = true;

      updateFontSizes();
    }
  };

  const updateLabelFontSize = (id: string, fontSize: number) => {
    labelFontSizes.current.set(id, fontSize);

    if (labelFontSizes.current.size === labelCount) {
      isLabelReady.current = true;

      updateFontSizes();
    }
  };

  const updateTrendFontSize = (id: string, fontSize: number) => {
    trendFontSizes.current.set(id, fontSize);

    if (trendFontSizes.current.size === trendCount) {
      isTrendReady.current = true;

      updateFontSizes();
    }
  };

  return (
    <GlobalFontSizeContext.Provider
      value={{
        valueFontSize: fontSizes?.value,
        labelFontSize: fontSizes?.label,
        trendFontSize: fontSizes?.trend,
        updateValueFontSize,
        updateLabelFontSize,
        updateTrendFontSize,
      }}
    >
      {children}
    </GlobalFontSizeContext.Provider>
  );
};
